/**
 * API 根地址。
 * - 默认空字符串：请求当前页面同源路径（开发时经 vue.config.js 代理到后端），局域网访问也不会误连「手机自己的 127.0.0.1」。
 * - 若需直连后端，可在 .env.* 中设置 VUE_APP_API_BASE=http://主机:端口（勿尾斜杠）。
 */
function resolveApiBase() {
  const raw = typeof process !== 'undefined' && process.env && process.env.VUE_APP_API_BASE
  if (raw === undefined || raw === null || raw === false) return ''
  const s = String(raw).trim()
  // 勿在 .env 写 VUE_APP_API_BASE=false，否则会变成字面路径 /false/api/...
  const lower = s.toLowerCase()
  if (!s || lower === 'false' || lower === 'undefined' || lower === 'null') return ''
  return s.replace(/\/$/, '')
}

export const BASE_URL = resolveApiBase()

/**
 * 展示/打开上传资源用的完整或同源路径。
 * - 库中一般为 /uploads/...；勿写死 127.0.0.1，否则外网本机浏览器会裂图。
 * - 若历史数据已是 http://127.0.0.1:8081/uploads/...，自动改成当前页面 origin。
 */
export function toMediaUrl(url) {
  const raw = String(url || '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw)
      const host = String(u.hostname || '').toLowerCase()
      if (host === '127.0.0.1' || host === 'localhost' || host === '0.0.0.0') {
        if (typeof window !== 'undefined' && window.location && window.location.origin) {
          return `${String(window.location.origin).replace(/\/$/, '')}${u.pathname || ''}${u.search || ''}`
        }
      }
    } catch (_) {
      /* ignore */
    }
    return raw
  }
  const path = raw.startsWith('/') ? raw : `/${raw}`
  const base = String(BASE_URL || '').replace(/\/$/, '')
  if (base) return `${base}${path}`
  return path
}

/** 胸卡二维码、外链等需要手机访问的 API 根地址（优先 VUE_APP_API_BASE，否则当前页 origin） */
export function resolvePublicApiBase() {
  const base = String(BASE_URL || '').replace(/\/$/, '')
  if (base) return base
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return String(window.location.origin).replace(/\/$/, '')
  }
  return ''
}

const ADMIN_TOKEN_KEY = 'admin.token'

async function request(path, options = {}) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const needAuth = path.startsWith('/api/admin') && path !== '/api/admin/auth/login'
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  const headers = isFormData
    ? { ...(options.headers || {}) }
    : { 'Content-Type': 'application/json', ...(options.headers || {}) }
  if (needAuth && token) {
    headers.Authorization = `Bearer ${token}`
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    headers,
    ...options,
    // 登录不依赖 Cookie；omit 可避免 localhost 多端口共用导致 Cookie 头过大触发后端 431
    ...(path === '/api/admin/auth/login' ? { credentials: 'omit' } : {}),
  })

  let body = null
  try {
    body = await res.json()
  } catch (e) {
    body = null
  }

  if (!res.ok) {
    if (res.status === 401 && needAuth) {
      localStorage.removeItem(ADMIN_TOKEN_KEY)
      localStorage.removeItem('admin.profile')
    }
    const msg = body?.message || body?.error || `HTTP ${res.status}`
    throw new Error(msg)
  }
  if (body && typeof body.code !== 'undefined' && body.code !== 0) {
    throw new Error(body.message || '请求失败')
  }
  return body?.data ?? body
}

export function getBanners() {
  return request('/api/admin/cms/banners')
}

export function createBanner(payload) {
  return request('/api/admin/cms/banners', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateBanner(id, payload) {
  return request(`/api/admin/cms/banners/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deleteBanner(id) {
  return request(`/api/admin/cms/banners/${id}`, {
    method: 'DELETE'
  })
}

export function getTiles() {
  return request('/api/admin/cms/tiles')
}

export function createTile(payload) {
  return request('/api/admin/cms/tiles', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateTile(id, payload) {
  return request(`/api/admin/cms/tiles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deleteTile(id, groupCode = '') {
  const q = groupCode ? `?groupCode=${encodeURIComponent(String(groupCode))}` : ''
  return request(`/api/admin/cms/tiles/${id}${q}`, {
    method: 'DELETE'
  })
}

/** 首页入口「页面类型」独立维护（不使用字典表） */
export function getTilePageTypes(params = {}) {
  const q = params.status ? `?status=${encodeURIComponent(params.status)}` : ''
  return request(`/api/admin/cms/tile-page-types${q}`)
}

export function createTilePageType(payload) {
  return request('/api/admin/cms/tile-page-types', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateTilePageType(id, payload) {
  return request(`/api/admin/cms/tile-page-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deleteTilePageType(id) {
  return request(`/api/admin/cms/tile-page-types/${id}`, {
    method: 'DELETE'
  })
}

export function getPageContents(options = {}) {
  const q = options.includeDeleted ? '?includeDeleted=true' : ''
  return request(`/api/admin/content/pages${q}`)
}

export function getPageContentById(id) {
  return request(`/api/admin/content/pages/${encodeURIComponent(String(id))}`)
}

/** @returns {{ exists: boolean, id?: number|null, isDeleted?: number|null }} */
export function getPageContentKeyExists(contentKey) {
  const k = encodeURIComponent(String(contentKey || '').trim())
  return request(`/api/admin/content/pages/exists?contentKey=${k}`)
}

export function createPageContent(payload) {
  return request('/api/admin/content/pages', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updatePageContent(id, payload) {
  return request(`/api/admin/content/pages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deletePageContent(id) {
  return request(`/api/admin/content/pages/${id}`, {
    method: 'DELETE'
  })
}

/** 软删恢复：对应表 site_page_content.is_deleted → 0 */
export function restorePageContent(id) {
  return request(`/api/admin/content/pages/${encodeURIComponent(String(id))}/restore`, {
    method: 'POST'
  })
}

export function getForms(params) {
  const category = params && params.category ? String(params.category).trim() : ''
  const q = category ? `?category=${encodeURIComponent(category)}` : ''
  return request(`/api/admin/forms${q}`)
}

export function getSatisfactionTemplates() {
  return request('/api/admin/satisfaction/templates')
}

export function getSatisfactionOverview(activityId) {
  return request(`/api/admin/satisfaction/activities/${encodeURIComponent(String(activityId))}/overview`)
}

export function getSatisfactionResponses(activityId) {
  return request(`/api/admin/satisfaction/activities/${encodeURIComponent(String(activityId))}/responses`)
}

export function getSatisfactionStats(activityId) {
  return request(`/api/admin/satisfaction/activities/${encodeURIComponent(String(activityId))}/stats`)
}

export function createForm(payload) {
  return request('/api/admin/forms', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateForm(id, payload) {
  return request(`/api/admin/forms/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deleteForm(id) {
  return request(`/api/admin/forms/${id}`, {
    method: 'DELETE'
  })
}

export function getFormFields(formId) {
  return request(`/api/admin/forms/${formId}/fields`)
}

export function getFormGroups(formId) {
  return request(`/api/admin/forms/${formId}/groups`)
}

export function createFormGroup(formId, payload) {
  return request(`/api/admin/forms/${formId}/groups`, {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateFormGroup(groupId, payload) {
  return request(`/api/admin/forms/groups/${groupId}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deleteFormGroup(groupId) {
  return request(`/api/admin/forms/groups/${groupId}`, {
    method: 'DELETE'
  })
}

export function reorderFormGroups(formId, groupIds) {
  return request(`/api/admin/forms/${formId}/groups/reorder`, {
    method: 'POST',
    body: JSON.stringify({ groupIds: Array.isArray(groupIds) ? groupIds : [] })
  })
}

export function createFormField(formId, payload) {
  return request(`/api/admin/forms/${formId}/fields`, {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateFormField(fieldId, payload) {
  return request(`/api/admin/forms/fields/${fieldId}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deleteFormField(fieldId) {
  return request(`/api/admin/forms/fields/${fieldId}`, {
    method: 'DELETE'
  })
}

/** 上传该表单的 Word 模板（.docx），multipart 字段名 file */
export function uploadFormWordTemplate(formId, file) {
  const fd = new FormData()
  fd.append('file', file)
  return request(`/api/admin/forms/${formId}/export/word-template`, {
    method: 'POST',
    body: fd
  })
}

/** 上传印章图（png/jpg/gif），multipart 字段名 file */
export function uploadFormSealImage(formId, file) {
  const fd = new FormData()
  fd.append('file', file)
  return request(`/api/admin/forms/${formId}/export/seal-image`, {
    method: 'POST',
    body: fd
  })
}

export function clearFormSealImage(formId) {
  return request(`/api/admin/forms/${formId}/export/seal-image`, {
    method: 'DELETE'
  })
}

export function getFormSubmissions(formId, page = 0, size = 20, filters = {}) {
  const q = new URLSearchParams({ page: String(page), size: String(size) })
  if (filters.applicantName) q.set('applicantName', String(filters.applicantName))
  if (filters.applicantUnit) q.set('applicantUnit', String(filters.applicantUnit))
  if (filters.reviewStatus) q.set('reviewStatus', String(filters.reviewStatus))
  if (filters.startTime) q.set('startTime', String(filters.startTime))
  if (filters.endTime) q.set('endTime', String(filters.endTime))
  return request(`/api/admin/forms/${formId}/submissions?${q.toString()}`)
}

export function getFormSubmissionDetail(submissionId, formId) {
  const q = new URLSearchParams({ formId: String(formId) })
  return request(`/api/admin/forms/submissions/${submissionId}?${q.toString()}`)
}

export function reviewFormSubmission(submissionId, payload) {
  return request(`/api/admin/forms/submissions/${submissionId}/review`, {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

/** 返回下载 URL（需带 token 时用 fetch/blob）；仅拼接地址可用 open */
export function formSubmissionExportWordUrl(submissionId, formId) {
  const q = new URLSearchParams({ formId: String(formId) })
  return `${BASE_URL}/api/admin/forms/submissions/${submissionId}/export-word?${q.toString()}`
}

export function getMembershipApplySubmissions(page = 0, size = 20, filters = {}) {
  const q = new URLSearchParams({ page: String(page), size: String(size) })
  if (filters.applicantName) q.set('applicantName', String(filters.applicantName))
  if (filters.applicantUnit) q.set('applicantUnit', String(filters.applicantUnit))
  if (filters.reviewStatus) q.set('reviewStatus', String(filters.reviewStatus))
  if (filters.startTime) q.set('startTime', String(filters.startTime))
  if (filters.endTime) q.set('endTime', String(filters.endTime))
  return request(`/api/admin/forms/membership-apply/submissions?${q.toString()}`)
}

export function getMembershipApplySubmissionDetail(submissionId) {
  return request(`/api/admin/forms/membership-apply/submissions/${submissionId}`)
}

/** 会员管理：无申请记录时创建空预录单并返回详情 */
export function ensureMembershipApplyForMember(memberId) {
  return request(`/api/admin/forms/membership-apply/ensure-for-member/${encodeURIComponent(String(memberId))}`, {
    method: 'POST',
  })
}

/** 一键修复：正式会员补表导致的 ACTIVE→PENDING / 误会费 / 职务不一致 */
export function repairSupplementaryMembershipApplyDirty() {
  return request('/api/admin/forms/membership-apply/repair-supplementary-dirty', {
    method: 'POST',
  })
}

export function updateMembershipApplySubmissionPayload(submissionId, payload) {
  return request(`/api/admin/forms/membership-apply/submissions/${submissionId}/payload`, {
    method: 'PUT',
    body: JSON.stringify({ payload: payload || {} })
  })
}

export function reviewMembershipApplySubmission(submissionId, payload) {
  return request(`/api/admin/forms/membership-apply/submissions/${submissionId}/review`, {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function reviewMembershipApplyPayment(submissionId, payload) {
  return request(`/api/admin/forms/membership-apply/submissions/${submissionId}/payment-review`, {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function attachMembershipApplyFeeVoucher(submissionId, payload = {}) {
  return request(`/api/admin/forms/membership-apply/submissions/${submissionId}/attach-fee-voucher`, {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function approveMembershipApplyWithNotices(submissionId, payload) {
  return request(`/api/admin/forms/membership-apply/submissions/${submissionId}/approve-with-notices`, {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function membershipApplySubmissionExportWordUrl(submissionId) {
  return `${BASE_URL}/api/admin/forms/membership-apply/submissions/${submissionId}/export-word`
}

export function getMembershipConfirmTemplates(status = '') {
  const q = status ? `?status=${encodeURIComponent(String(status))}` : ''
  return request(`/api/admin/membership/confirm-templates${q}`)
}

export function createMembershipConfirmTemplate(payload) {
  return request('/api/admin/membership/confirm-templates', {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function updateMembershipConfirmTemplate(id, payload) {
  return request(`/api/admin/membership/confirm-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload || {})
  })
}

export function deleteMembershipConfirmTemplate(id) {
  return request(`/api/admin/membership/confirm-templates/${id}`, {
    method: 'DELETE'
  })
}

/** 确认函模版占位 + 上次填写值（发送弹框用） */
export function getMembershipConfirmFillSchema(templateId) {
  const id = Number(templateId || 0)
  if (!id) return Promise.reject(new Error('请选择确认函模板'))
  return request(`/api/admin/membership/confirm-templates/${id}/fill-schema`)
}

/** 活动胸卡模版 */
export function getBadgeTemplates(status = '') {
  const q = status ? `?status=${encodeURIComponent(String(status))}` : ''
  return request(`/api/admin/badge-templates${q}`)
}

export function createBadgeTemplate(payload) {
  return request('/api/admin/badge-templates', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export function updateBadgeTemplate(id, payload) {
  return request(`/api/admin/badge-templates/${encodeURIComponent(String(id))}`, {
    method: 'PUT',
    body: JSON.stringify(payload || {}),
  })
}

export function deleteBadgeTemplate(id) {
  return request(`/api/admin/badge-templates/${encodeURIComponent(String(id))}`, { method: 'DELETE' })
}

export function previewBadgeTemplate(id, queryString = '') {
  const q = queryString ? (queryString.startsWith('?') ? queryString : `?${queryString}`) : ''
  return request(`/api/admin/badge-templates/${encodeURIComponent(String(id))}/preview${q}`)
}

export function getAdminAttendeeNameBadge(activityId, { memberId, slotIndex = 0, submissionId } = {}) {
  const p = new URLSearchParams({ memberId: String(memberId) })
  if (slotIndex != null) p.set('slotIndex', String(slotIndex))
  if (submissionId != null && submissionId > 0) p.set('submissionId', String(submissionId))
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/attendee-name-badge?${p.toString()}`,
  )
}

export function batchSendMembershipConfirmNotice(payload) {
  return request('/api/admin/membership/confirm-notices/batch-send', {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function sendMembershipConfirmNoticeToMembers(payload) {
  return request('/api/admin/membership/confirm-notices/send-to-members', {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

/** 缴费函模板管理 */
export function getMembershipFeeTemplates(status = '') {
  const q = status ? `?status=${encodeURIComponent(String(status))}` : ''
  return request(`/api/admin/membership/fee-templates${q}`)
}

export function getMembershipFeeSettings() {
  return request('/api/admin/membership/fee-settings')
}

export function createMembershipFeeTemplate(payload) {
  return request('/api/admin/membership/fee-templates', {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function updateMembershipFeeTemplate(id, payload) {
  return request(`/api/admin/membership/fee-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload || {})
  })
}

export function deleteMembershipFeeTemplate(id) {
  return request(`/api/admin/membership/fee-templates/${id}`, {
    method: 'DELETE'
  })
}

export function sendMembershipFeeNoticeToMembers(payload) {
  return request('/api/admin/membership/fee-notices/send-to-members', {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

/** 会费定价（按会员级别） */
export function getMembershipFeePrices(status = '') {
  const q = status ? `?status=${encodeURIComponent(String(status))}` : ''
  return request(`/api/admin/membership/fee-prices${q}`)
}

export function createMembershipFeePrice(payload) {
  return request('/api/admin/membership/fee-prices', {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function updateMembershipFeePrice(id, payload) {
  return request(`/api/admin/membership/fee-prices/${encodeURIComponent(String(id))}`, {
    method: 'PUT',
    body: JSON.stringify(payload || {})
  })
}

export function deleteMembershipFeePrice(id) {
  return request(`/api/admin/membership/fee-prices/${encodeURIComponent(String(id))}`, {
    method: 'DELETE'
  })
}

export function getSupplyPostsForReview() {
  return request('/api/admin/supply/posts')
}

export function reviewSupplyPost(id, payload) {
  return request(`/api/admin/supply/posts/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateSupplyPostStatus(id, statusCode) {
  return request(`/api/admin/supply/posts/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ statusCode }),
  })
}

export function reviewSupplyRevision(id, payload) {
  return request(`/api/admin/supply/posts/${id}/revision/review`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function getSupplyIntentsForAdmin(params = {}) {
  const parts = []
  if (params.status) parts.push(`status=${encodeURIComponent(params.status)}`)
  if (params.q) parts.push(`q=${encodeURIComponent(params.q)}`)
  const qs = parts.length ? `?${parts.join('&')}` : ''
  return request(`/api/admin/supply/intents${qs}`)
}

export function updateSupplyIntentStatusAdmin(id, statusCode) {
  return request(`/api/admin/supply/intents/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ statusCode }),
  })
}

export function uploadImage(file) {
  const fd = new FormData()
  fd.append('file', file)
  return request('/api/admin/upload/image', {
    method: 'POST',
    body: fd
  })
}

/** PDF 等通用文件（表彰公示）；后端需实现 /api/admin/upload/file，与图片接口类似返回 { url } */
export function uploadFile(file) {
  const fd = new FormData()
  fd.append('file', file)
  return request('/api/admin/upload/file', {
    method: 'POST',
    body: fd
  })
}

/** 首页热门活动（多条；小程序取排序最前且启用的一条） */
export function getAdminHomeHotActivities() {
  return request('/api/admin/cms/home/hot-activities')
}

export function createAdminHomeHotActivity(payload) {
  return request('/api/admin/cms/home/hot-activities', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateAdminHomeHotActivity(id, payload) {
  return request(`/api/admin/cms/home/hot-activities/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deleteAdminHomeHotActivity(id) {
  return request(`/api/admin/cms/home/hot-activities/${id}`, {
    method: 'DELETE'
  })
}

/** 首页横向相册卡片 */
export function getAdminHomeAlbums() {
  return request('/api/admin/cms/home/albums')
}

export function getAdminHomeAlbum(id) {
  return request(`/api/admin/cms/home/albums/${encodeURIComponent(String(id))}`)
}

export function getAdminAlbumTabs() {
  return request('/api/admin/cms/home/album-tabs')
}

export function createAdminAlbumTab(tabLabel) {
  return request('/api/admin/cms/home/album-tabs', {
    method: 'POST',
    body: JSON.stringify({ tabLabel: String(tabLabel || '').trim() }),
  })
}

export function renameAdminAlbumTab(id, tabLabel) {
  return request(`/api/admin/cms/home/album-tabs/${encodeURIComponent(String(id))}`, {
    method: 'PUT',
    body: JSON.stringify({ tabLabel: String(tabLabel || '').trim() }),
  })
}

export function reorderAdminAlbumTabs(orderedLabels) {
  return request('/api/admin/cms/home/album-tabs/reorder', {
    method: 'PUT',
    body: JSON.stringify({ orderedLabels: Array.isArray(orderedLabels) ? orderedLabels : [] }),
  })
}

export function deleteAdminAlbumTab(id) {
  return request(`/api/admin/cms/home/album-tabs/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
  })
}

export function getAdminHomeHotActivityRegistrations(id) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(id))}/registrations`)
}

export function createAdminHomeAlbum(payload) {
  return request('/api/admin/cms/home/albums', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateAdminHomeAlbum(id, payload) {
  return request(`/api/admin/cms/home/albums/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export function deleteAdminHomeAlbum(id) {
  return request(`/api/admin/cms/home/albums/${id}`, {
    method: 'DELETE'
  })
}

export function pinAdminHomeAlbum(id, pinned) {
  return request(`/api/admin/cms/home/albums/${id}/pin`, {
    method: 'POST',
    body: JSON.stringify({ pinned: !!pinned })
  })
}

/** 小程序活动（列表 + 详情主数据） */
export function getAdminCmsActivities(query = {}) {
  const params = new URLSearchParams()
  const q = query && typeof query === 'object' ? query : {}
  Object.entries(q).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') params.set(k, String(v).trim())
  })
  const qs = params.toString()
  return request(`/api/admin/cms/activities${qs ? `?${qs}` : ''}`)
}

/** 文字地址 → 经纬度（需后端配置腾讯地图 WebService Key） */
export function getAdminCmsActivityGeocode(address) {
  const q = encodeURIComponent(String(address || '').trim())
  return request(`/api/admin/cms/activities/geocode?address=${q}`)
}

export function getAdminCmsActivity(id) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(id))}`)
}

export function getAdminCmsActivityViewLogs(id) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(id))}/view-logs`)
}

/** 可选活动报名表单定义（form_definition，已启用且非入会表） */
export function listAdminActivitySignupFormOptions() {
  return request('/api/admin/cms/activities/signup-form-definition-options')
}

export function createAdminCmsActivity(payload) {
  return request('/api/admin/cms/activities', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export function updateAdminCmsActivity(id, payload) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(id))}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

/** 生成小程序签到场外链参数（7 日内有效），与 app.activity-checkin.scene-secret 一致方可校验通过 */
export function getAdminActivityCheckinSceneLink(activityId) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/checkin-scene-link`)
}

export function fetchAdminVenueCheckinQr(activityId) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/venue-checkin-qr`)
}

export function fetchAdminGuestSignupQr(activityId) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/guest-signup-qr`)
}

/** 活动分享入口小程序码（无邀请人，扫码进活动详情） */
export function fetchAdminActivityShareWxaQr(activityId) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/share-wxa-code`)
}

export function deleteAdminCmsActivity(id) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(id))}`, {
    method: 'DELETE'
  })
}

export function getAdminCmsActivityRegistrations(id) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(id))}/registrations`)
}

/** 报名名单主表：按参会人扁平化，一人一行 */
export function getAdminCmsActivityRegistrationAttendees(id) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(id))}/registration-attendees`)
}

export function getAdminActivitySeatingPlan(activityId) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/plan`)
}

export function upsertAdminActivitySeatingPlan(activityId, body) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/plan`, {
    method: 'PUT',
    body: JSON.stringify(body || {}),
  })
}

export function getAdminActivitySeatingEligibleAttendees(activityId) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/eligible-attendees`)
}

export function saveAdminActivitySeatingAssignments(activityId, items) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/assignments`, {
    method: 'PUT',
    body: JSON.stringify(items || []),
  })
}

export function publishAdminActivitySeatingPlan(activityId) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/publish`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

/** 下载报名名单导入模板（Excel） */
export async function downloadAdminActivitySignupImportTemplate(activityId) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const res = await fetch(
    `${BASE_URL}/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-import/template`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  )
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = await res.json()
      msg = j?.message || j?.error || msg
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `activity-${activityId}-signup-import-template.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** 批量导入报名名单（导入后直接通过审核） */
export async function importAdminActivitySignupRoster(activityId, file) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(
    `${BASE_URL}/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-import`,
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    },
  )
  let body = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  if (!res.ok) {
    throw new Error(body?.message || body?.error || `HTTP ${res.status}`)
  }
  return body?.data != null ? body.data : body
}

export function lookupAdminProxySignupMember(activityId, phone) {
  const q = encodeURIComponent(String(phone || '').trim())
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/proxy-signup/member-lookup?phone=${q}`,
  )
}

export function postAdminProxySignup(activityId, attendees) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/proxy-signup`, {
    method: 'POST',
    body: JSON.stringify({ attendees: attendees || [] }),
  })
}

export function fetchAdminActivitySeatingHolds(activityId) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/holds`)
}

export function saveAdminActivitySeatingHolds(activityId, items) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/holds`, {
    method: 'PUT',
    body: JSON.stringify(items || []),
  })
}

export function deleteAdminActivitySeatingHold(activityId, holdId) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/holds/${encodeURIComponent(String(holdId))}`,
    { method: 'DELETE' },
  )
}

/** 从 Excel/CSV 导入排座 */
export async function importAdminActivitySeatingAssignments(activityId, file) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(
    `${BASE_URL}/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/seating/assignments/import`,
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    },
  )
  let body = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  if (!res.ok) {
    throw new Error(body?.message || body?.error || `HTTP ${res.status}`)
  }
  return body?.data != null ? body.data : body
}

/** 后台粘贴会员电子凭证核验串代签到 */
export function staffCheckinActivity(activityId, token) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/staff-checkin`,
    { method: 'POST', body: JSON.stringify({ token: String(token || '').trim() }) },
  )
}

/** 后台编辑报名：获取可编辑 payload（已去掉审核元数据） */
export function getAdminActivitySignupRawPayload(activityId, submissionId) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-submissions/${encodeURIComponent(String(submissionId))}/raw-payload`,
  )
}

/** 后台编辑报名：保存整单 payload */
export function updateAdminActivitySignupPayload(activityId, submissionId, payload) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-submissions/${encodeURIComponent(String(submissionId))}/payload`,
    { method: 'PUT', body: JSON.stringify(payload || {}) },
  )
}

/** 下载活动已签到参会人名单 CSV */
export async function downloadAdminActivityCheckedInRosterCsv(activityId) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const res = await fetch(
    `${BASE_URL}/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/checkin-roster-export`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  )
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = await res.json()
      msg = j?.message || j?.error || msg
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `activity-${activityId}-checked-in.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** 下载活动报名名单 CSV（浏览器保存文件） */
export async function downloadAdminActivityRosterCsv(activityId) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const res = await fetch(
    `${BASE_URL}/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/roster-export`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  )
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = await res.json()
      msg = j?.message || j?.error || msg
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `activity-${activityId}-roster.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function fetchAdminActivityRosterExportFields(activityId) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/roster-export-fields`,
  )
}

/** 自定义字段导出报名名单 Excel（含当前筛选条件） */
export async function downloadAdminActivityRosterExportXlsx(activityId, { fields = [], filter = {} } = {}) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const q = new URLSearchParams()
  const fieldList = Array.isArray(fields) ? fields.filter(Boolean) : []
  if (fieldList.length) q.set('fields', fieldList.join(','))
  const f = filter && typeof filter === 'object' ? filter : {}
  const add = (key, val) => {
    const s = String(val == null ? '' : val).trim()
    if (s) q.set(key, s)
  }
  add('identity', f.identity)
  add('attendeeKeyword', f.attendeeKeyword)
  add('submitter', f.submitter)
  add('activityStatus', f.activityStatus)
  add('orderStatus', f.orderStatus)
  add('submittedFrom', f.submittedFrom)
  add('submittedTo', f.submittedTo)
  add('cancelledFrom', f.cancelledFrom)
  add('cancelledTo', f.cancelledTo)
  const tagId = Number(f.tagId || 0)
  if (tagId > 0) q.set('tagId', String(tagId))
  const qs = q.toString()
  const url = `${BASE_URL}/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/roster-export-xlsx${qs ? `?${qs}` : ''}`
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = await res.json()
      msg = j?.message || j?.error || msg
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = `activity-${activityId}-roster.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objUrl)
}

/** 驳回会员本场活动报名（整账号全部报名表；须后端实现同名接口） */
export function rejectAdminActivityMemberSignup(activityId, payload) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/member-signup/reject`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

/** 后台通过报名审核：生成/更新待支付活动费订单（须后端实现） */
export function approveAdminActivityMemberSignup(activityId, payload) {
  return request(`/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/member-signup/approve`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

/** 后台逐条审核通过某次报名提交（全部提交行通过后，再调 member-signup/approve 生成待支付订单） */
export function approveAdminActivitySignupSubmission(activityId, submissionId, payload = {}) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-submissions/${encodeURIComponent(String(submissionId))}/approve`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  )
}

/** 后台逐条驳回某次报名提交 */
export function rejectAdminActivitySignupSubmission(activityId, submissionId, payload = {}) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-submissions/${encodeURIComponent(String(submissionId))}/reject`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  )
}

/** 会务通过会员取消报名申请 */
export function approveAdminSignupCancelRequest(activityId, submissionId, slotIndex) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-submissions/${encodeURIComponent(String(submissionId))}/attendees/${encodeURIComponent(String(slotIndex))}/approve-cancel-request`,
    { method: 'POST', body: JSON.stringify({}) },
  )
}

/** 会务驳回会员取消报名申请（须填写驳回原因） */
export function rejectAdminSignupCancelRequest(activityId, submissionId, slotIndex, payload = {}) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-submissions/${encodeURIComponent(String(submissionId))}/attendees/${encodeURIComponent(String(slotIndex))}/reject-cancel-request`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  )
}

/** 后台为某参会人槽位补传/替换电子签名（已签到不可重扫时） */
export function assignAdminAttendeeCheckinSignature(activityId, submissionId, slotIndex, payload = {}) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/signup-submissions/${encodeURIComponent(String(submissionId))}/attendees/${encodeURIComponent(String(slotIndex))}/checkin-signature`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  )
}

/** 后台确认活动费已退回 → 订单状态 REFUNDED */
export function markAdminActivityFeeOrderRefunded(activityId, orderId, payload = {}) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/orders/${encodeURIComponent(String(orderId))}/mark-refunded`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  )
}

/** 该会员本场待业务审核的活动费订单列表（须后端实现） */
export function listAdminActivityMemberOrdersPending(activityId, memberId) {
  const mid = encodeURIComponent(String(memberId || ''))
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/member-orders-pending?memberId=${mid}`,
  )
}

/** 订单审核通过 → 订单已完成、会员可待参加（须后端实现） */
export function approveAdminActivityOrderCompletion(activityId, orderId, payload = {}) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/orders/${encodeURIComponent(String(orderId))}/approve-completion`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  )
}

/** 同意会员「取消订单」申请（须后端实现） */
export function approveAdminActivityOrderCancelRequest(activityId, orderId, payload = {}) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/orders/${encodeURIComponent(String(orderId))}/approve-cancel`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  )
}

export function getAdminMemberOrders(params = {}) {
  const q = new URLSearchParams()
  if (params.memberId) q.set('memberId', String(params.memberId))
  if (params.orderType) q.set('orderType', String(params.orderType))
  if (params.status) q.set('status', String(params.status))
  const suffix = q.toString() ? `?${q.toString()}` : ''
  return request(`/api/admin/member-orders${suffix}`)
}

/** 线下凭证审核通过 → 已支付 */
export function approveAdminMemberOrderOfflinePay(id, payload = {}) {
  return request(`/api/admin/member-orders/${encodeURIComponent(String(id))}/approve-offline-pay`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

/** 为正式会员生成/更新年度续费待支付订单 */
export function ensureAdminMemberRenewalOrder(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/ensure-renewal-order`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export function getAdminMemberRenewalSettings(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/renewal-settings`)
}

export function previewAdminMemberRenewalSettings(memberId, renewalLevel, startDate) {
  const parts = []
  const lv = String(renewalLevel || '').trim()
  const sd = String(startDate || '').trim()
  if (lv) parts.push(`renewalLevel=${encodeURIComponent(lv)}`)
  if (sd) parts.push(`startDate=${encodeURIComponent(sd)}`)
  const qs = parts.length ? `?${parts.join('&')}` : ''
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/renewal-settings/preview${qs}`)
}

export function submitAdminMemberRenewalSettings(memberId, payload = {}) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/renewal-settings`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

/** 某会员的会费 + 活动费缴费记录 */
export async function getAdminMemberPaymentRecords(memberId) {
  const id = encodeURIComponent(String(memberId))
  try {
    return await request(`/api/admin/member-orders?memberId=${id}`)
  } catch (e) {
    const msg = String((e && e.message) || '')
    if (!/404|not found/i.test(msg)) throw e
    return request(`/api/admin/members/${id}/payment-records`)
  }
}

/** 后台代传活动费线下缴费凭证（报名审核通过、订单待支付） */
export function submitAdminActivityFeeOfflineVoucher(activityId, orderId, payload = {}) {
  return request(
    `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/orders/${encodeURIComponent(String(orderId))}/submit-offline-voucher`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  )
}

const adminActivityLotteryBase = (activityId) =>
  `/api/admin/cms/activities/${encodeURIComponent(String(activityId))}/lottery`

export function getAdminActivityLottery(activityId) {
  return request(adminActivityLotteryBase(activityId))
}

export function putAdminActivityLotteryEnabled(activityId, enabled) {
  return request(`${adminActivityLotteryBase(activityId)}/enabled?enabled=${enabled ? 'true' : 'false'}`, {
    method: 'PUT',
  })
}

export function postAdminActivityLotteryRegenerateToken(activityId) {
  return request(`${adminActivityLotteryBase(activityId)}/screen-token/regenerate`, { method: 'POST' })
}

export function postAdminActivityLotteryPrize(activityId, body) {
  return request(`${adminActivityLotteryBase(activityId)}/prizes`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  })
}

export function postAdminActivityLotteryDraw(activityId, prizeId) {
  return request(`${adminActivityLotteryBase(activityId)}/prizes/${encodeURIComponent(String(prizeId))}/draw`, {
    method: 'POST',
  })
}

export function postAdminActivityLotteryConfirmDraw(activityId, drawId) {
  return request(`${adminActivityLotteryBase(activityId)}/draws/${encodeURIComponent(String(drawId))}/confirm`, {
    method: 'POST',
  })
}

export function postAdminActivityLotteryVoidDraw(activityId, drawId, reason) {
  return request(`${adminActivityLotteryBase(activityId)}/draws/${encodeURIComponent(String(drawId))}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || '' }),
  })
}

export function postAdminActivityLotteryVoidWinner(activityId, winnerId, reason) {
  return request(`${adminActivityLotteryBase(activityId)}/winners/${encodeURIComponent(String(winnerId))}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || '' }),
  })
}

export function postAdminActivityLotteryRedeemWinner(activityId, winnerId) {
  return request(`${adminActivityLotteryBase(activityId)}/winners/${encodeURIComponent(String(winnerId))}/redeem`, {
    method: 'POST',
  })
}

/** 驳回线下凭证（须填写原因） */
export function rejectAdminMemberOrderOfflinePay(id, payload) {
  return request(`/api/admin/member-orders/${encodeURIComponent(String(id))}/reject-offline-pay`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

/** 待支付/被驳回：会务代传线下凭证 */
export function submitAdminMemberOrderOfflineVoucher(id, payload = {}) {
  return request(`/api/admin/member-orders/${encodeURIComponent(String(id))}/submit-offline-voucher`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

/** 凭证待审核阶段补录真实凭证（如小程序线上 MOCK 支付） */
export function attachAdminMemberOrderVoucher(id, payload = {}) {
  return request(`/api/admin/member-orders/${encodeURIComponent(String(id))}/attach-voucher`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export function getAdminStatsOverview() {
  return request('/api/admin/stats/overview')
}

function engagementRangeQuery(params = {}) {
  const q = new URLSearchParams()
  if (params.start) q.set('start', String(params.start))
  if (params.end) q.set('end', String(params.end))
  if (params.granularity) q.set('granularity', String(params.granularity))
  if (params.keyword) q.set('keyword', String(params.keyword))
  if (params.status) q.set('status', String(params.status))
  if (params.page != null && params.page !== '') q.set('page', String(params.page))
  if (params.pageSize != null && params.pageSize !== '') q.set('pageSize', String(params.pageSize))
  if (params.sort) q.set('sort', String(params.sort))
  if (params.order) q.set('order', String(params.order))
  if (params.type) q.set('type', String(params.type))
  if (params.phone) q.set('phone', String(params.phone))
  const suffix = q.toString() ? `?${q.toString()}` : ''
  return suffix
}

export function getAdminEngagementOverview(params = {}) {
  return request(`/api/admin/stats/engagement/overview${engagementRangeQuery(params)}`)
}

export function getAdminEngagementTrend(params = {}) {
  return request(`/api/admin/stats/engagement/trend${engagementRangeQuery(params)}`)
}

export function getAdminEngagementComposition(params = {}) {
  return request(`/api/admin/stats/engagement/composition${engagementRangeQuery(params)}`)
}

export function getAdminMemberEngagementSummary(params = {}) {
  return request(`/api/admin/members/engagement/summary${engagementRangeQuery(params)}`)
}

export function getAdminMemberEngagementDetails(memberId, params = {}) {
  const suffix = engagementRangeQuery(params)
  return request(`/api/admin/members/engagement/${encodeURIComponent(String(memberId))}/details${suffix}`)
}

/** 后台全局待办提醒（右下角消息栈） */
export function getAdminPendingNotifications() {
  return request('/api/admin/notifications/pending')
}

/** 后台操作日志 */
export function getAdminOperationLogs(params = {}) {
  const q = new URLSearchParams()
  if (params.adminUserId != null && params.adminUserId !== '') q.set('adminUserId', String(params.adminUserId))
  if (params.action) q.set('action', String(params.action))
  if (params.targetType) q.set('targetType', String(params.targetType))
  if (params.targetId != null && params.targetId !== '') q.set('targetId', String(params.targetId))
  if (params.keyword) q.set('keyword', String(params.keyword))
  if (params.operatorKeyword) q.set('operatorKeyword', String(params.operatorKeyword))
  if (params.startAt) q.set('startAt', String(params.startAt))
  if (params.endAt) q.set('endAt', String(params.endAt))
  if (params.limit != null && params.limit !== '') q.set('limit', String(params.limit))
  const suffix = q.toString() ? `?${q.toString()}` : ''
  return request(`/api/admin/operation-logs${suffix}`)
}

/** 后台认证 */
export function adminLogin(payload) {
  return request('/api/admin/auth/login', { method: 'POST', body: JSON.stringify(payload) })
}

export function getAdminMe() {
  return request('/api/admin/auth/me')
}

export function adminLogout() {
  return request('/api/admin/auth/logout', { method: 'POST' })
}

/** 修改当前登录管理员密码（校验旧密码） */
export function adminChangePassword(payload) {
  return request('/api/admin/auth/change-password', { method: 'POST', body: JSON.stringify(payload || {}) })
}

/** 超级管理员重置他人后台密码（无需旧密码） */
export function resetRbacUserPassword(id, newPassword) {
  return request(`/api/admin/rbac/users/${id}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ newPassword: String(newPassword || '').trim() }),
  })
}

/** 超级管理员删除后台用户（逻辑删除，并清空其服务专员绑定） */
export function deleteRbacUser(id) {
  return request(`/api/admin/rbac/users/${id}`, { method: 'DELETE' })
}

/** 字典类型 */
export function getDictTypes() {
  return request('/api/admin/dict/types')
}

export function createDictType(payload) {
  return request('/api/admin/dict/types', { method: 'POST', body: JSON.stringify(payload) })
}

export function updateDictType(id, payload) {
  return request(`/api/admin/dict/types/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
}

export function deleteDictType(id) {
  return request(`/api/admin/dict/types/${id}`, { method: 'DELETE' })
}

export function reorderDictTypes(orderedIds) {
  return request('/api/admin/dict/types/reorder', {
    method: 'PUT',
    body: JSON.stringify({ orderedIds: orderedIds || [] }),
  })
}

/** 字典项：statusCode 可选，用于下拉只取启用项 */
export function getDictItems(typeId, statusCode) {
  const q = new URLSearchParams()
  if (typeId != null && typeId !== '') q.set('typeId', String(typeId))
  if (statusCode) q.set('statusCode', String(statusCode))
  const suffix = q.toString() ? `?${q.toString()}` : ''
  return request(`/api/admin/dict/items${suffix}`)
}

export function createDictItem(payload) {
  return request('/api/admin/dict/items', { method: 'POST', body: JSON.stringify(payload) })
}

export function updateDictItem(id, payload) {
  return request(`/api/admin/dict/items/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
}

export function deleteDictItem(id) {
  return request(`/api/admin/dict/items/${id}`, { method: 'DELETE' })
}

export function reorderDictItems(typeId, orderedIds) {
  return request('/api/admin/dict/items/reorder', {
    method: 'PUT',
    body: JSON.stringify({ typeId, orderedIds: orderedIds || [] }),
  })
}

/** 页面目标库（供 LINK_TYPE 等字典项选择用） */
export function getPageTargets(statusCode) {
  const q = statusCode ? `?statusCode=${encodeURIComponent(statusCode)}` : ''
  return request(`/api/admin/dict/page-targets${q}`)
}

/** 从小程序 pages.json 自动解析的页面候选 */
export function getPageTargetCandidates() {
  return request('/api/admin/dict/page-target-candidates')
}

export function createPageTarget(payload) {
  return request('/api/admin/dict/page-targets', { method: 'POST', body: JSON.stringify(payload || {}) })
}

export function updatePageTarget(id, payload) {
  return request(`/api/admin/dict/page-targets/${encodeURIComponent(String(id))}`, { method: 'PUT', body: JSON.stringify(payload || {}) })
}

export function deletePageTarget(id) {
  return request(`/api/admin/dict/page-targets/${encodeURIComponent(String(id))}`, { method: 'DELETE' })
}

/** 某字典项绑定的页面目标编码列表 */
export function getDictItemPageTargets(itemId) {
  return request(`/api/admin/dict/items/${encodeURIComponent(String(itemId))}/page-targets`)
}

/** 保存某字典项绑定的页面目标（全量覆盖） */
export function updateDictItemPageTargets(itemId, targetCodes = []) {
  return request(`/api/admin/dict/items/${encodeURIComponent(String(itemId))}/page-targets`, {
    method: 'PUT',
    body: JSON.stringify({ targetCodes: targetCodes || [] })
  })
}

/** 资讯栏目（与小程序 article/list?code= 对应） */
export function getArticleCategories() {
  return request('/api/admin/cms/article-categories')
}

export function createArticleCategory(payload) {
  return request('/api/admin/cms/article-categories', { method: 'POST', body: JSON.stringify(payload) })
}

export function updateArticleCategory(id, payload) {
  return request(`/api/admin/cms/article-categories/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
}

export function deleteArticleCategory(id) {
  return request(`/api/admin/cms/article-categories/${id}`, { method: 'DELETE' })
}

export function getArticles(categoryId) {
  return request(`/api/admin/cms/articles?categoryId=${encodeURIComponent(categoryId)}`)
}

export function createArticle(payload) {
  return request('/api/admin/cms/articles', { method: 'POST', body: JSON.stringify(payload) })
}

export function updateArticle(id, payload) {
  return request(`/api/admin/cms/articles/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
}

export function deleteArticle(id) {
  return request(`/api/admin/cms/articles/${id}`, { method: 'DELETE' })
}

export function resolveArticleTitleByUrl(url) {
  return request(`/api/admin/cms/articles/resolve-title?url=${encodeURIComponent(url)}`)
}

/** 资讯管理合并版：树形结构 + 宫格绑定 */
export function getArticleManageTree() {
  return request('/api/admin/cms/article-manage/tree')
}

export function syncArticleManageFromTiles() {
  return request('/api/admin/cms/article-manage/sync-from-tiles', { method: 'POST' })
}

export function saveArticleManageCategory(payload, isUpdate = false) {
  return request('/api/admin/cms/article-manage/categories', {
    method: isUpdate ? 'PUT' : 'POST',
    body: JSON.stringify(payload),
  })
}

export function saveArticleManageNav(payload, isUpdate = false) {
  return request('/api/admin/cms/article-manage/nav-tabs', {
    method: isUpdate ? 'PUT' : 'POST',
    body: JSON.stringify(payload),
  })
}

export function deleteArticleManageNav(id) {
  return request(`/api/admin/cms/article-manage/nav-tabs/${encodeURIComponent(String(id))}`, { method: 'DELETE' })
}

/** 注册者个人信息归档列表 */
export function getAdminMemberMapProfiles() {
  return request('/api/admin/member-map-profiles')
}

/** 注册者归档详情（含地图坐标说明等） */
export function getAdminMemberMapProfileDetail(id) {
  return request(`/api/admin/member-map-profiles/${encodeURIComponent(String(id))}`)
}

export function putAdminMemberMapProfile(memberId, payload) {
  return request(`/api/admin/member-map-profiles/${encodeURIComponent(String(memberId))}`, {
    method: 'PUT',
    body: JSON.stringify(payload || {}),
  })
}

/** 注册者档案：是否为人脉群聊管理员 */
export function patchAdminMemberNetworkChatAdmin(memberId, enabled) {
  return request(`/api/admin/member-map-profiles/${encodeURIComponent(String(memberId))}/network-chat-admin`, {
    method: 'PUT',
    body: JSON.stringify({ enabled: !!enabled }),
  })
}

/** 将 /api/admin/members 的 data 统一为数组（兼容分页对象 { content/list/... }） */
export function unwrapAdminMembersResponse(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  for (const k of ['content', 'list', 'records', 'items', 'rows', 'members', 'elements']) {
    const v = data[k]
    if (Array.isArray(v)) return v
  }
  if (data.data !== undefined) return unwrapAdminMembersResponse(data.data)
  return []
}

/** 会员管理 */
export function getAdminMembers(params = {}) {
  const q = new URLSearchParams()
  if (params.keyword) q.set('keyword', params.keyword)
  if (params.status) q.set('status', params.status)
  if (params.tagId) q.set('tagId', String(params.tagId))
  const suffix = q.toString() ? `?${q.toString()}` : ''
  return request(`/api/admin/members${suffix}`)
}

/** 会员入会申请盖章扫描件（文件） */
export function getAdminMemberMembershipApplySealFile(memberId) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/membership-apply-seal-file`,
  )
}

/** PC 代管单位成员信息（正式/待审），同源小程序会务 personnel */
export function getAdminMemberPersonnel(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/personnel`)
}

export function approveAdminMemberPersonnelRoster(memberId, rosterId) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/personnel/roster/${encodeURIComponent(String(rosterId))}/approve`,
    { method: 'POST', body: '{}' },
  )
}

export function rejectAdminMemberPersonnelRoster(memberId, rosterId) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/personnel/roster/${encodeURIComponent(String(rosterId))}/reject`,
    { method: 'POST', body: '{}' },
  )
}

export function removeAdminMemberPersonnelRoster(memberId, rosterId) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/personnel/roster/${encodeURIComponent(String(rosterId))}/remove`,
    { method: 'POST', body: '{}' },
  )
}

export function updateAdminMemberPersonnelRoster(memberId, rosterId, payload) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/personnel/roster/${encodeURIComponent(String(rosterId))}`,
    { method: 'PUT', body: JSON.stringify(payload || {}) },
  )
}

export function getAdminMemberManagedMembers(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/managed-members`)
}

export function createAdminMemberManagedMember(memberId, payload) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/managed-members`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export function updateAdminMemberManagedMember(memberId, personId, payload) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/managed-members/${encodeURIComponent(String(personId))}`,
    { method: 'PUT', body: JSON.stringify(payload || {}) },
  )
}

export function deleteAdminMemberManagedMember(memberId, personId) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/managed-members/${encodeURIComponent(String(personId))}`,
    { method: 'DELETE' },
  )
}

export function createAdminMember(payload) {
  return request('/api/admin/members', { method: 'POST', body: JSON.stringify(payload) })
}

/** Download Excel template for member bulk import (admin Bearer). */
export async function downloadAdminMemberImportTemplate() {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  if (!token) {
    throw new Error('未登录或登录已过期，请重新登录后再下载模板')
  }
  const res = await fetch(`${BASE_URL}/api/admin/members/import/template`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const ct = String(res.headers.get('content-type') || '').toLowerCase()
  if (!res.ok) {
    let msg = `下载失败（HTTP ${res.status}）`
    try {
      const j = await res.json()
      if (j && (j.message || j.error)) msg = j.message || j.error
    } catch {
      // ignore
    }
    if (res.status === 403 || /权限不足/.test(msg)) {
      msg = `${msg}。请确认账号具备「会员管理-查看/编辑」权限后重新登录再试`
    }
    throw new Error(msg)
  }
  const buf = await res.arrayBuffer()
  if (ct.includes('application/json') || (buf.byteLength > 0 && buf.byteLength < 200)) {
    try {
      const text = new TextDecoder().decode(buf)
      if (text.trim().startsWith('{')) {
        const j = JSON.parse(text)
        throw new Error(j.message || j.error || '下载失败')
      }
    } catch (e) {
      if (e && e.message && !/Unexpected|JSON/.test(e.message)) throw e
    }
  }
  if (!buf || buf.byteLength < 64) {
    throw new Error('模板内容为空，请重启后端后再试')
  }
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const filename = 'member_import_template.xlsx'

  // 优先「另存为」：不依赖站点「下载」权限（Edge 常因站点下载被禁显示「需有下载权限」）
  if (typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'Excel 工作簿',
            accept: {
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            },
          },
        ],
      })
      const writable = await handle.createWritable()
      await writable.write(blob)
      await writable.close()
      return
    } catch (e) {
      if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError')) {
        if (e.name === 'AbortError') return
      }
      // 继续走传统下载
    }
  }

  // IE/旧 Edge
  if (typeof navigator !== 'undefined' && typeof navigator.msSaveOrOpenBlob === 'function') {
    navigator.msSaveOrOpenBlob(blob, filename)
    return
  }

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    try {
      a.remove()
    } catch (_) {
      /* ignore */
    }
    URL.revokeObjectURL(url)
  }, 2500)
}

function memberImportResultBytesFromBase64(base64) {
  if (!base64) throw new Error('无导入结果文件')
  const bin = atob(String(base64))
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}

/** 当前浏览器是否支持「打开并写回原文件」（Chrome / Edge 等） */
export function canWriteBackMemberImportFile() {
  return typeof window !== 'undefined' && typeof window.showOpenFilePicker === 'function'
}

/**
 * 弹出系统文件选择框，返回可读写的 FileSystemFileHandle + File。
 * 用户取消时抛出 name=AbortError。
 */
export async function pickAdminMemberImportXlsxForWriteBack() {
  if (!canWriteBackMemberImportFile()) {
    throw new Error('当前浏览器不支持写回原文件，请使用 Chrome / Edge，或改用普通上传')
  }
  const [handle] = await window.showOpenFilePicker({
    types: [
      {
        description: 'Excel 工作簿',
        accept: {
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        },
      },
    ],
    excludeAcceptAllOption: true,
    multiple: false,
  })
  const opts = { mode: 'readwrite' }
  if (typeof handle.queryPermission === 'function') {
    let perm = await handle.queryPermission(opts)
    if (perm !== 'granted' && typeof handle.requestPermission === 'function') {
      perm = await handle.requestPermission(opts)
    }
    if (perm !== 'granted') {
      throw new Error('未获得文件写入权限，无法写回原文件')
    }
  }
  const file = await handle.getFile()
  return { handle, file }
}

function friendlyMemberImportWriteError(err) {
  const raw = String((err && err.message) || err || '')
  if (/state cached|state had changed|NotAllowedError|NotFoundError|NoModificationAllowed/i.test(raw)) {
    return '原文件可能正在被 Excel 打开，或磁盘上的文件已变化，无法直接覆盖'
  }
  return raw || '无法写回原文件'
}

/**
 * 将导入结果写回用户选中的原文件（覆盖）。
 * @param {FileSystemFileHandle} fileHandle
 * @param {string} base64
 */
export async function writeAdminMemberImportResultToFileHandle(fileHandle, base64) {
  if (!fileHandle || typeof fileHandle.createWritable !== 'function') {
    throw new Error('无法写回原文件')
  }
  const bytes = memberImportResultBytesFromBase64(base64)
  try {
    const writable = await fileHandle.createWritable({ keepExistingData: false })
    try {
      await writable.write(bytes)
    } finally {
      await writable.close()
    }
  } catch (e) {
    throw new Error(friendlyMemberImportWriteError(e))
  }
}

/**
 * 弹出「另存为」让用户选路径覆盖（优先于自动下载生成 member_import (1).xlsx）。
 * @returns {'saved'|'aborted'}
 */
export async function saveAdminMemberImportResultWithPicker(base64, suggestedName = 'member_import.xlsx') {
  const bytes = memberImportResultBytesFromBase64(base64)
  const safeName =
    String(suggestedName || 'member_import.xlsx').trim() || 'member_import.xlsx'
  if (typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: safeName,
        types: [
          {
            description: 'Excel 工作簿',
            accept: {
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            },
          },
        ],
      })
      const writable = await handle.createWritable({ keepExistingData: false })
      try {
        await writable.write(bytes)
      } finally {
        await writable.close()
      }
      return 'saved'
    } catch (e) {
      if (e && (e.name === 'AbortError' || /cancel|取消/i.test(String(e.message || '')))) {
        return 'aborted'
      }
      throw e
    }
  }
  await openAdminMemberImportResultXlsx(base64, safeName)
  return 'saved'
}

/**
 * 将导入结果 Excel（base64）下载一次（仅作最终兜底）。
 * @param {string} base64
 * @param {string} [filename]
 */
export async function openAdminMemberImportResultXlsx(base64, filename = 'member_import_result.xlsx') {
  const bytes = memberImportResultBytesFromBase64(base64)
  const mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  const blob = new Blob([bytes], { type: mime })
  const safeName = String(filename || 'member_import_result.xlsx').trim() || 'member_import_result.xlsx'

  // 旧 Edge：一次「打开 / 保存」对话框
  if (typeof navigator !== 'undefined' && typeof navigator.msSaveOrOpenBlob === 'function') {
    navigator.msSaveOrOpenBlob(blob, safeName)
    return
  }

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = safeName
  document.body.appendChild(a)
  a.click()
  try {
    a.remove()
  } catch (_) {
    /* ignore */
  }
  setTimeout(() => {
    try {
      URL.revokeObjectURL(url)
    } catch (_) {
      /* ignore */
    }
  }, 4000)
}

/**
 * Import members from Excel (.xlsx).
 * @param {File} file
 */
export async function postAdminMembersImport(file) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${BASE_URL}/api/admin/members/import`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form
  })
  let body = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  if (!res.ok) {
    const msg = body?.message || body?.error || `HTTP ${res.status}`
    throw new Error(msg)
  }
  if (body && typeof body.code !== 'undefined' && body.code !== 0) {
    throw new Error(body.message || '请求失败')
  }
  return body?.data ?? body
}

export function updateAdminMember(id, payload) {
  return request(`/api/admin/members/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
}

export function getAdminMemberSessions(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/sessions`)
}

export function revokeAdminMemberSessions(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/revoke-sessions`, { method: 'POST' })
}

/** 永久删除已退会会员账号（非已退会会报错） */
export function deleteAdminWithdrawnMember(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}`, { method: 'DELETE' })
}

export function reviewAdminMemberFeeOrder(orderId, payload) {
  return request(`/api/admin/members/fee-orders/${encodeURIComponent(String(orderId))}/review`, {
    method: 'POST',
    body: JSON.stringify(payload || {})
  })
}

export function getAdminMemberAuthIdentities(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/auth-identities`)
}

export function getAdminMemberAuthIdentityLogs(memberId) {
  return request(`/api/admin/members/${encodeURIComponent(String(memberId))}/auth-identities/logs`)
}

export function unbindAdminMemberAuthIdentity(memberId, identityId) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/auth-identities/${encodeURIComponent(String(identityId))}/unbind`,
    { method: 'POST' }
  )
}

/** 超级管理员设置/重置会员小程序登录密码（无本地账号时自动开通） */
export function resetAdminMemberLocalPassword(memberId, password) {
  return request(
    `/api/admin/members/${encodeURIComponent(String(memberId))}/local-account/reset-password`,
    {
      method: 'POST',
      body: JSON.stringify({ password: String(password || '').trim() }),
    }
  )
}

/** 会员自定义标签 */
export function getAdminMemberTags() {
  return request('/api/admin/member-tags')
}

export function createAdminMemberTag(tagName) {
  return request('/api/admin/member-tags', {
    method: 'POST',
    body: JSON.stringify({ tagName }),
  })
}

export function assignAdminMemberTags(payload) {
  return request('/api/admin/member-tags/assign', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

/** RBAC */
export function getPermissionCatalog() {
  return request('/api/admin/rbac/permissions-catalog')
}

export function getWebPermissionsCatalog() {
  return request('/api/admin/rbac/web-permissions-catalog')
}

export function getRbacRoles() {
  return request('/api/admin/rbac/roles')
}

export function createRbacRole(payload) {
  return request('/api/admin/rbac/roles', { method: 'POST', body: JSON.stringify(payload) })
}

export function updateRbacRole(id, payload) {
  return request(`/api/admin/rbac/roles/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
}

export function getRbacUsers() {
  return request('/api/admin/rbac/users')
}

export function createRbacUser(payload) {
  return request('/api/admin/rbac/users', { method: 'POST', body: JSON.stringify(payload) })
}

export function updateRbacUser(id, payload) {
  return request(`/api/admin/rbac/users/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
}

export function getStaffMpPermissionsCatalog() {
  return request('/api/admin/staff/mp-permissions-catalog')
}

export function getServiceConsultants() {
  return request('/api/admin/staff/service-consultants')
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token)
  else localStorage.removeItem(ADMIN_TOKEN_KEY)
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || ''
}

/** 人脉撮合 / 人脉订单 */
export function listNetworkConnectionRequests(params = {}) {
  const q = new URLSearchParams()
  if (params.sourceScope) q.set('sourceScope', params.sourceScope)
  if (params.stageCode) q.set('stageCode', params.stageCode)
  if (params.auditStatus) q.set('auditStatus', params.auditStatus)
  if (params.applicantMobile) q.set('applicantMobile', params.applicantMobile)
  if (params.createdAtFrom) q.set('createdAtFrom', params.createdAtFrom)
  if (params.createdAtTo) q.set('createdAtTo', params.createdAtTo)
  const qs = q.toString()
  return request(`/api/admin/network-connection-requests${qs ? `?${qs}` : ''}`)
}

export function getNetworkConnectionRequestDetail(id) {
  return request(`/api/admin/network-connection-requests/${id}`)
}

export function reviewNetworkConnectionRequest(id, payload) {
  return request(`/api/admin/network-connection-requests/${id}/review`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export function listNetworkChatAdminOptions() {
  return request('/api/admin/network-connection-requests/chat-admin-options')
}

export function updateNetworkConnectionChatParticipants(id, payload) {
  return request(`/api/admin/network-connection-requests/${id}/chat-participants`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export function getNetworkResourceConfig() {
  return request('/api/admin/network-resource-config')
}

export function saveNetworkResourceConfig(payload) {
  return request('/api/admin/network-resource-config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export function getServiceFormCatalog() {
  return request('/api/admin/service-forms/catalog?category=SERVICE')
}

export function getServiceFormSubmissions(params = {}) {
  const q = new URLSearchParams({ category: 'SERVICE' })
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') q.set(k, String(v).trim())
  })
  const qs = q.toString()
  return request(`/api/admin/service-forms/submissions${qs ? `?${qs}` : ''}`)
}

export function getServiceFormSubmission(id) {
  return request(`/api/admin/service-forms/submissions/${encodeURIComponent(String(id))}`)
}

export function updateServiceFormSubmission(id, payload) {
  return request(`/api/admin/service-forms/submissions/${encodeURIComponent(String(id))}`, {
    method: 'PUT',
    body: JSON.stringify({ payload: payload || {} }),
  })
}

export function getServiceFormReportSummary(params = {}) {
  const q = new URLSearchParams()
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') q.set(k, String(v).trim())
  })
  const qs = q.toString()
  return request(`/api/admin/service-forms/reports/summary${qs ? `?${qs}` : ''}`)
}

export function getServiceFormReportRows(params = {}) {
  const q = new URLSearchParams()
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') q.set(k, String(v).trim())
  })
  const qs = q.toString()
  return request(`/api/admin/service-forms/reports/rows${qs ? `?${qs}` : ''}`)
}

export async function downloadServiceFormReport(params = {}) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const q = new URLSearchParams()
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') q.set(k, String(v).trim())
  })
  const qs = q.toString()
  const res = await fetch(`${BASE_URL}/api/admin/service-forms/reports/export${qs ? `?${qs}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = await res.json()
      if (j && j.message) msg = j.message
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') || ''
  const m = /filename="([^"]+)"/i.exec(cd)
  const filename = m && m[1] ? m[1] : '会员服务对接登记.xlsx'
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

export function getServiceFormReportTemplateInfo() {
  return request('/api/admin/service-forms/reports/template')
}

export async function downloadServiceFormReportTemplate() {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const res = await fetch(`${BASE_URL}/api/admin/service-forms/reports/template/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = await res.json()
      if (j && j.message) msg = j.message
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = 'service-form-report-template.xlsx'
  a.click()
  URL.revokeObjectURL(a.href)
}

export async function uploadServiceFormReportTemplate(file) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${BASE_URL}/api/admin/service-forms/reports/template`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  })
  const j = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error((j && j.message) || `HTTP ${res.status}`)
  }
  return j.data != null ? j.data : j
}

export function clearServiceFormReportTemplate() {
  return request('/api/admin/service-forms/reports/template', { method: 'DELETE' })
}

/** 消息通知（发送记录 / 被动模板 / 新建群发） */
export function getMessageManualTemplateGroups() {
  return request('/api/admin/messages/templates/manual-groups')
}

export function getMessageTemplates() {
  return request('/api/admin/messages/templates')
}

export function getPassiveMessageTemplates() {
  return request('/api/admin/messages/templates/passive')
}

export function updatePassiveMessageTemplateEnabled(code, enabled) {
  return request(`/api/admin/messages/templates/${encodeURIComponent(String(code))}/passive-enabled`, {
    method: 'PUT',
    body: JSON.stringify({ enabled: !!enabled }),
  })
}

export function movePassiveMessageTemplateSort(code, direction) {
  return request(`/api/admin/messages/templates/${encodeURIComponent(String(code))}/passive-sort`, {
    method: 'PUT',
    body: JSON.stringify({ direction: direction === 'down' ? 'down' : 'up' }),
  })
}

export function getMessageSmsBalance() {
  return request('/api/admin/messages/sms-balance')
}

export function listMessageCampaigns(scope = 'all') {
  const q = encodeURIComponent(String(scope || 'all'))
  return request(`/api/admin/messages/campaigns?scope=${q}`)
}

export function getMessageCampaign(id) {
  return request(`/api/admin/messages/campaigns/${encodeURIComponent(String(id))}`)
}

export function getMessageRecipients(campaignId) {
  return request(`/api/admin/messages/campaigns/${encodeURIComponent(String(campaignId))}/recipients`)
}

export function createMessageCampaign(body) {
  return request('/api/admin/messages/campaigns', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  })
}

export function resendFailedMessages(campaignId, recipientIds) {
  return request(`/api/admin/messages/campaigns/${encodeURIComponent(String(campaignId))}/resend`, {
    method: 'POST',
    body: JSON.stringify({ recipientIds: recipientIds || [] }),
  })
}

export function searchMessageMemberCandidates(keyword) {
  const q = encodeURIComponent(String(keyword || ''))
  return request(`/api/admin/messages/member-candidates?keyword=${q}`)
}

export function searchMessageMemberPicker(body) {
  return request('/api/admin/messages/member-picker/search', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  })
}

export function selectAllMessageMemberPicker(body) {
  return request('/api/admin/messages/member-picker/select-all', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  })
}

export function listMessageRelatedActivities(keyword = '') {
  const q = encodeURIComponent(String(keyword || ''))
  return request(`/api/admin/messages/related-content/activities?keyword=${q}`)
}

export function listMessageRelatedAlbums(keyword = '') {
  const q = encodeURIComponent(String(keyword || ''))
  return request(`/api/admin/messages/related-content/albums?keyword=${q}`)
}

export function listMessageRelatedArticles(keyword = '') {
  const q = encodeURIComponent(String(keyword || ''))
  return request(`/api/admin/messages/related-content/articles?keyword=${q}`)
}

export function testSendMessage(body) {
  return request('/api/admin/messages/test-send', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  })
}

export function getCooperationProfileAdmin() {
  return request('/api/admin/cooperation/profile')
}

export function saveCooperationProfileAdmin(body) {
  return request('/api/admin/cooperation/profile', {
    method: 'PUT',
    body: JSON.stringify(body || {}),
  })
}

export function listCooperationRecordsAdmin(params = {}) {
  const q = new URLSearchParams()
  if (params.mainTab) q.set('mainTab', params.mainTab)
  if (params.direction) q.set('direction', params.direction)
  q.set('page', String(params.page || 1))
  q.set('pageSize', String(params.pageSize || 20))
  return request(`/api/admin/cooperation/records?${q.toString()}`)
}

export function getCooperationDetailAdmin(id) {
  return request(`/api/admin/cooperation/records/${encodeURIComponent(String(id))}`)
}

export function createCooperationRecordAdmin(body) {
  return request('/api/admin/cooperation/records', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  })
}

export function convertCooperationPlanAdmin(id) {
  return request(`/api/admin/cooperation/records/${encodeURIComponent(String(id))}/convert`, { method: 'POST' })
}

export function reviewCooperationRecordAdmin(id, approved, reviewNote) {
  return request(`/api/admin/cooperation/records/${encodeURIComponent(String(id))}/review`, {
    method: 'PUT',
    body: JSON.stringify({ approved: !!approved, reviewNote: reviewNote || '' }),
  })
}

export function withdrawCooperationRecordAdmin(id) {
  return request(`/api/admin/cooperation/records/${encodeURIComponent(String(id))}/withdraw`, { method: 'PUT' })
}

export function requestCooperationTerminateAdmin(id) {
  return request(`/api/admin/cooperation/records/${encodeURIComponent(String(id))}/terminate-request`, { method: 'PUT' })
}

export function confirmCooperationTerminateAdmin(id) {
  return request(`/api/admin/cooperation/records/${encodeURIComponent(String(id))}/terminate-confirm`, { method: 'PUT' })
}

export function deleteCooperationPlanAdmin(id) {
  return request(`/api/admin/cooperation/records/${encodeURIComponent(String(id))}`, { method: 'DELETE' })
}

export function updateCooperationRecordAdmin(id, body) {
  return request(`/api/admin/cooperation/records/${encodeURIComponent(String(id))}`, {
    method: 'PUT',
    body: JSON.stringify(body || {}),
  })
}

